import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useChain } from '../../demo/selectors/chain'
import { useDemoStore } from '../../demo/store'
import { Card, GlossaryText, Toast, ToastHost } from '../../ui'
import { renewalData } from './data'
import styles from './RenewalPage.module.css'

function activateWithKeyboard(event: KeyboardEvent<HTMLAnchorElement>, action: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  action()
}

function ArrowIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  )
}

function rowValue(key: string): ReactNode {
  if (key === 'quota') return <>{renewalData.quotaFrom} <span className={styles.mutedArrow}>→</span> <b>{renewalData.quotaTo}</b></>
  if (key === 'product') return renewalData.addedProduct
  if (key === 'date') return renewalData.copy.effectiveLabel
  return <GlossaryText text={renewalData.resultLine} paren />
}

export function RenewalPlan() {
  const navigate = useNavigate()
  const [toast, setToast] = useState<string | null>(null)
  const confirmRenewal = useDemoStore((state) => state.confirmRenewal)
  const step = useChain().steps.find((item) => item.id === '08')
  const done = step?.status === 'done'

  const confirm = () => {
    confirmRenewal()
    setToast(renewalData.confirmToast)
  }
  const adjust = () => { void navigate('/partnership') }

  return (
    <>
      <div className={styles.scope} data-scope="renewal-plan">
        <Card>
          <div className={styles.cardHead}>
            <span className={`${styles.statusChip} ${done ? styles.statusDone : styles.statusPending}`}>第 08 步</span>
            <h3>{renewalData.copy.cardTitle}</h3>
            <span className={styles.cardSubtitle}>{renewalData.stepSubtitle}</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.planNote} data-role="plan-note">
              {renewalData.copy.noteLine1}<b>{renewalData.copy.noteStrong}</b>{renewalData.copy.noteLine1Tail}<br />
              {renewalData.copy.noteLine2}
            </div>
            <table className={styles.planTable} data-role="plan-table">
              <tbody>
                {renewalData.rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className={row.key === 'quota' ? 'mono-num' : undefined}>{rowValue(row.key)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.actionRow}>
              <a
                className={`${styles.action} ${styles.primaryAction}`}
                data-role="confirm"
                role="button"
                tabIndex={0}
                onClick={confirm}
                onKeyDown={(event) => activateWithKeyboard(event, confirm)}
              >
                {renewalData.copy.confirmLabel}<ArrowIcon />
              </a>
              <a
                className={`${styles.action} ${styles.ghostAction}`}
                data-role="adjust"
                role="button"
                tabIndex={0}
                onClick={adjust}
                onKeyDown={(event) => activateWithKeyboard(event, adjust)}
              >
                {renewalData.copy.adjustLabel}
              </a>
              <span className={styles.budgetNote}>{renewalData.copy.budgetNote}</span>
            </div>
          </div>
        </Card>
      </div>
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </>
  )
}
